import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  useLoading,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Badge } from 'baseui/badge';
import { Button } from 'baseui/button';
import { ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { ONLY_DATE_FORMAT } from '../../../constants/app';
import { TABLE_COLUMNS_NAME } from '../../../constants/drivers';
import { FLEET_DRIVER_PAGE } from '../../../constants/fleet';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { apiParamsState, workspaceState } from '../../../storage/app';
import { FleetDriver, FleetDriverTab } from '../../../types/drivers';
import { isUnknownDriver } from '../../../utils/drivers';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import { StyledTooltip } from '../../ui/StyledTooltip';
import { useFleetDrivers } from '../hooks/useFleetDrivers';
import FleetDriverAvatar from './driver/FleetDriverAvatar';
import FleetDriverModal from './driver/FleetDriverModal';

function FleetDriverList() {
  const { theme, css, classes } = useStyles();
  const workspace = useRecoilValue(workspaceState);
  const apiParams = useRecoilValue(apiParamsState);
  const [drivers, setDrivers] = useState<FleetDriver[]>();
  const {
    page,
    onChangePage,
    loadFleetDrivers,
    reload,
    search,
    filters: { status },
    onReload,
  } = useFleetDrivers();
  const [totalItems, setTotalItems] = useState(0);
  const { formatDateFromTs } = useMoment();
  const { loading, stopLoading, startLoading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const [selectedDriver, setSelectedDriver] = useState<FleetDriver>();
  const { openModal, closeModal, showModal } = useModal();
  const navigate = useNavigate();

  useUpdateEffect(() => {
    onChangePage(0);
  }, [search, reload, status, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (page === 0) {
      onChangePage(1);
      return;
    }
    const loadData = async () => {
      startLoading();
      await loadDrivers();
      stopLoading();
    };

    loadData();
  }, [page]);

  const loadDrivers = async () => {
    const { drivers, error, total } = await loadFleetDrivers();
    if (error) {
      showFailFeedback(error);
      setDrivers(undefined);
      setTotalItems(0);
    } else {
      setDrivers(drivers);
      setTotalItems(total ?? 0);
    }
  };

  const onOpenDetailModal = (driver: FleetDriver) => {
    setSelectedDriver(driver);
    openModal();
  };

  const onCloseDetailModal = () => {
    closeModal();
    setSelectedDriver(undefined);
  };

  const formatOnlyDateFromTs = (ts: number) => {
    return formatDateFromTs(ts, ONLY_DATE_FORMAT);
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!drivers && !loading && <EmptyState title="Todavía no cuentas con conductores." />}
      {!drivers && loading && <Loading />}
      {drivers && (
        <>
          {!!drivers?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              data={drivers.map((driver) => {
                return {
                  items: [
                    <FleetDriverAvatar
                      key={`avatar-${driver._id}`}
                      name={driver.name}
                      lastName={driver.lastName}
                      photo={driver.photo}
                      size="50px"
                      fontSize="20px"
                    />,
                    <FlexColumn
                      key={`name-${driver._id}`}
                      gap={0}
                    >
                      <ParagraphSmall margin={0}>{driver.name || '-'}</ParagraphSmall>
                      {isUnknownDriver(driver) && (
                        <Badge
                          content="Pendiente de identificar"
                          color="negative"
                          overrides={{
                            Badge: {
                              style: {
                                fontSize: theme.typography.ParagraphXSmall.fontSize,
                              },
                            },
                          }}
                        />
                      )}
                    </FlexColumn>,
                    driver.lastName || '-',
                    driver.phone || '-',
                    driver.email || '-',
                    driver.licenseNumber || '-',
                    driver.licenseExpiry ? formatOnlyDateFromTs(driver.licenseExpiry) : '-',
                    formatOnlyDateFromTs(driver.createdAt),
                    formatOnlyDateFromTs(driver.updatedAt),
                    <FlexRow
                      gap={theme.sizing.scale200}
                      key={`details-${driver._id}`}
                    >
                      {workspace && (
                        <StyledTooltip
                          content="Ver detalles"
                          showArrow={false}
                        >
                          <Button
                            size="mini"
                            shape="square"
                            onClick={() => {
                              const route = generatePath(FLEET_DRIVER_PAGE.route, {
                                id: workspace._id,
                                driverId: driver._id,
                                tab: FleetDriverTab.general,
                              });
                              navigate(route, { replace: false });
                            }}
                            kind="tertiary"
                          >
                            <MaterialIcon
                              name="visibility"
                              size={theme.sizing.scale700}
                            />
                          </Button>
                        </StyledTooltip>
                      )}
                      <StyledTooltip
                        content="Editar"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenDetailModal(driver)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="edit"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>
                    </FlexRow>,
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={onChangePage}
              loading={loading}
            />
          )}
          {!drivers.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      {selectedDriver && (
        <FleetDriverModal
          isOpen={showModal}
          onClose={(update) => {
            if (update) onReload();
            onCloseDetailModal();
          }}
          driver={selectedDriver}
        />
      )}
    </FlexColumn>
  );
}

export default FleetDriverList;
